<template>
  <div id="main-container">
    <h1>Data Transfer Pipeline</h1>
    <div>Select which area of the data transfer pipeline you'd like to view!</div>

    <div id="data-options-container">
      <div>
        <button @click="feature = 'viewTables'" class="primary-button-sm-global">View Existing Tables</button>
      </div>
      <div>
        <button @click="feature = 'createSchemas'" class="primary-button-sm-global">Create Schemas</button>
      </div>
      <div>
        <button @click="feature = 'syncDatabases'" class="primary-button-sm-global">Sync Databases</button>
      </div>
    </div>

    <div v-if="feature == 'viewTables'" id="viewTables-container">
      <nav>
        <div v-for="(item, index) in databaseList" @click="assignViewTable(item)" :key="index">
          {{ item }}
        </div>
      </nav>
      <div>
        <table v-if="tableColumnViewList.length > 0">
          <thead>
            <tr>
              <th v-for="(columnName, index2) in tableColumnViewList" :key="`${index2}-column`">{{ columnName }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rowObject, index3) in tableRowViewList" :key="`${index3}-row`">
              <td v-for="(columnName, index4) in tableColumnViewList" :key="`${index3}-${index4}-cell`">
                {{ rowObject[columnName] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="feature == 'createSchemas'">
      <!-- Insert schema creation here -->
    </div>

    <!-- Sync Databases menu, utilizes db-sync API from backend -->
    <div v-else-if="feature == 'syncDatabases'" id="sync-container">
      <div class="sync-card">
        <h2>Database Synchronization</h2>
        <p class="sync-description">
          Synchronize data across cloud databases. <br>
          Upload mode syncs from hub to cloud.
        </p>

        <div class="sync-controls">
          <div class="sync-options">
            <label>
              <input type="radio" v-model="syncMode" value="upload" @change="updateSyncHistory">
              Upload Mode
            </label>
            <label>
              <input type="radio" v-model="syncMode" value="download" @change="updateSyncHistory">
              Download Mode
            </label>
          </div>

          <button @click="syncDatabases" :disabled="isSyncing" class="primary-button-sm-global sync-button">
            {{ isSyncing ? 'Syncing...' : 'Run Sync' }}
          </button>
        </div>

        <!-- Success Message with Animation -->
        <div v-if="syncSuccess" class="success-banner">
          <div class="success-content">
            <span class="success-icon">✅</span>
            <div>
              <h3 class="success-title">Success!</h3>
              <p class="success-message">{{ successMessage }}</p>
            </div>
            <button @click="dismissSuccess" class="dismiss-button">×</button>
          </div>
        </div>

        <!-- Status Message (for errors and info) -->
        <div v-if="syncStatus && !syncSuccess" class="sync-status" :class="syncStatusType">
          {{ syncStatus }}
        </div>

        <!-- Response Details -->
        <div v-if="syncResponse" class="sync-response">
          <h4>Response Details:</h4>
          <pre>{{ syncResponse }}</pre>
        </div>
      </div>

      <div v-if="recentLogs && recentLogs.length > 0">
        <h3>Recent Sync {{ syncMode === 'upload' ? 'Upload' : 'Download' }} History</h3>

        <!-- Table view -->
        <table class="sync-history-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Time</th>
              <th>Status</th>
              <th>Tables Changed</th>
              <th>Total Rows Changed</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(log, index) in recentLogs" :key="index" :class="log.status">
              <td>{{ index + 1 }}</td>
              <td>{{ formatTimestamp(log.timestamp) }}</td>
              <td>
                <span :class="['status-badge', log.status]">
                  {{ log.status === 'success' ? '✅ Success' : '❌ Failed' }}
                </span>
              </td>
              <td>
                <span v-if="log.status === 'success' && log.tables && log.tables.length > 0">
                  {{log.tables.map(t => t.name || t).join(', ')}}
                </span>
                <span v-else-if="log.status === 'success' && log.tables && log.tables.length === 0">
                  No tables changed
                </span>
                <span v-else>N/A</span>
              </td>
              <td>{{ log.totalRowsChanged || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p>No sync history available</p>
      </div>
    </div>
  </div>
</template>

<script>
import instance from '../axios.js';

export default {
  data() {
    return {
      feature: 'options', //options; viewTables; createSchemas; syncDatabases
      databaseList: [],
      tableRowViewList: [],
      tableColumnViewList: [],
      // Sync related data
      recentLogs: [],
      selectedLog: null,
      syncMode: 'upload',
      isSyncing: false,
      syncStatus: '',
      syncStatusType: '',
      syncResponse: null,
      syncSuccess: false,
      successMessage: '',
    }
  },
  mounted() {
    // Run on component mount
    this.updateSyncHistory();
  },
  async created() {
    try {
      let response = await instance.get('/api/developer-tools/tableNames');
      this.databaseList = response.data;
    } catch (e) {
      console.error(e);
    }
  },
  methods: {
    async assignViewTable(tableName) {
      try {
        let responseColumns = await instance.get(`/api/developer-tools/tableColumns/${tableName}`);
        this.tableColumnViewList = responseColumns.data;
        let responseRows = await instance.get(`/api/developer-tools/tableRows/${tableName}`);
        this.tableRowViewList = responseRows.data;
      } catch (e) {
        console.error(e);
      }
    },

    // Generate success message for demo purposes
    generateSuccessMessage(tableCount, entryCount) {
      const modeText = this.syncMode === 'upload' ? 'uploaded' : 'downloaded';
      return `Successfully ${modeText} ${entryCount} entries across ${tableCount} tables!`;
    },

    // Fetches past log information for db-sync API,
    // then transforms it into row view from most recent to least
    async updateSyncHistory() {
      this.syncStatus = 'Updating sync logs...';

      const logsResponse = await instance.get('http://localhost:8090/api/db-sync/logs', {
        params: {
          mode: this.syncMode,
          lines: 1000,
          format: 'text'
        }
      });
      const logText = logsResponse.data; // Returns as one big string, very scary

      const logs = [];

      // Split by the start marker for each log
      let individualLogs = logText.split(`===== sync ${this.syncMode} started `);

      for (let i = 0; i < individualLogs.length; i++) {
        let individualLog = individualLogs[i];

        // Extract the start timestamp (found in the first line)
        const timestampMatch = individualLog.match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
        const timestamp = timestampMatch ? timestampMatch[1] : 'Unknown time';

        // Check if the log errored out (no successful sync line)
        if (!individualLog.includes(`===== sync ${this.syncMode} finished `)) {
          // Build error log entry
          const logEntry = {
            timestamp: timestamp,
            tables: null,
            totalRowsChanged: null,
            status: 'error',
          };

          logs.push(logEntry);
          continue;
        }

        // Regex to find all table sync lines
        const tableRegex = /Table (\w+) scanned (\d+) rows; changed (\d+) rows across (\d+) columns\./g;
        // Use it to find all matches
        const tableLines = individualLog.matchAll(tableRegex);

        const tables = [];
        for (const match of tableLines) {
          const tableName = match[1];
          // const rowsScanned = parseInt(match[2]);
          const rowsChanged = parseInt(match[3]);
          // const columnsChanged = parseInt(match[4]);

          // Only add table if it has more than 0 rows changed
          if (rowsChanged > 0) {
            tables.push(tableName);
          }
        }

        // Extract total counts from summary line 
        const summaryMatch = individualLog.match(/Sync summary: scanned (\d+) rows; changed (\d+) rows/);
        // const totalRowsScanned = parseInt(summaryMatch[1]);
        const totalRowsChanged = parseInt(summaryMatch[2]);

        // Build successful log entry
        const logEntry = {
          timestamp: timestamp,
          tables: tables,
          totalRowsChanged: totalRowsChanged,
          status: 'success',
        };

        logs.push(logEntry);
      }

      // Sort logs by timestamp (most recent first)
      logs.sort((a, b) => b.timestamp - a.timestamp);

      // Store the parsed logs for use in the UI
      // Only display the top 10 most recent logs
      this.recentLogs = logs.slice(0, 10);
      this.syncStatus = '';
      this.syncStatusType = 'success';

      // Return the parsed logs
      return logs;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return 'Unknown time';
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    async syncDatabases() {
      this.isSyncing = true;
      this.syncStatus = 'Starting sync...';
      this.syncStatusType = 'info';
      this.syncResponse = null;
      this.syncSuccess = false;

      try {
        // Step 1: Run the sync for realsies
        const syncResponse = await instance.post('http://localhost:8090/api/db-sync/run', {
          mode: this.syncMode
        });

        // Step 2: Update sync changelogs
        this.updateSyncHistory();

        // Step 3: Generate success message
        this.successMessage = this.generateSuccessMessage(
          syncResponse.data.tables_synced || 0,
          syncResponse.data.entries_synced || 0
        );
        this.syncSuccess = true;
        this.syncStatus = '';
        this.syncResponse = syncResponse.data;

      } catch (error) {
        console.error('Sync failed:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message;
        this.syncStatus = 'Sync failed: ' + errorMessage;
        this.syncStatusType = 'error';
        this.syncResponse = error.response && error.response.data ? error.response.data : error.message;
        this.syncSuccess = false;

      } finally {
        this.isSyncing = false;
      }
    },

    dismissSuccess() {
      this.syncSuccess = false;
      this.syncStatus = '';
      this.syncResponse = null;
      this.successMessage = '';
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'sharedSCSS/globalSharedVariables';

#main-container {
  display: flex;
  flex-direction: column;
  text-align: center;

  #data-options-container {
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 10px;

    button {
      margin: 0 5px;
    }
  }

  #viewTables-container {
    display: flex;
    justify-content: center;
    column-gap: 2rem;

    >nav {
      display: flex;
      flex-direction: column;
      text-align: left;

      div {
        cursor: pointer;
        margin-bottom: 5px;
        border-radius: 5px;
        padding: 5px;

        &:hover {
          background-color: globalSharedVariables.$brand-200;
        }
      }
    }

    >div {
      overflow: auto;
    }
  }

  #sync-container {
    display: flex;
    justify-content: center;
    padding: 20px;

    .sync-card {
      background: white;
      border-radius: 8px;
      padding: 30px;
      max-width: 600px;
      width: 100%;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      text-align: left;

      h2 {
        margin-top: 0;
        color: globalSharedVariables.$brand-600;
      }

      .sync-description {
        color: #666;
        margin-bottom: 20px;
      }

      .sync-controls {
        display: flex;
        flex-direction: column;
        gap: 15px;
        margin: 20px 0;

        .sync-options {
          display: flex;
          gap: 20px;

          label {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;

            input[type="radio"] {
              cursor: pointer;
            }
          }
        }

        .sync-button {
          align-self: flex-start;
          padding: 10px 30px;

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }

      // Success Banner
      .success-banner {
        background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
        border-radius: 8px;
        padding: 20px;
        margin: 20px 0;
        animation: slideDown 0.5s ease-out;
        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);

        .success-content {
          display: flex;
          align-items: center;
          gap: 15px;
          color: white;

          .success-icon {
            font-size: 2.5rem;
            animation: bounce 0.6s ease;
          }

          .success-title {
            margin: 0;
            font-size: 1.4rem;
            font-weight: bold;
          }

          .success-message {
            margin: 5px 0 0 0;
            opacity: 0.9;
          }

          .dismiss-button {
            background: none;
            border: none;
            color: white;
            font-size: 1.8rem;
            cursor: pointer;
            margin-left: auto;
            padding: 0 8px;
            opacity: 0.7;
            transition: opacity 0.2s;

            &:hover {
              opacity: 1;
            }
          }
        }
      }

      // Animations
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }

        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes bounce {
        0% {
          transform: scale(0.3);
        }

        50% {
          transform: scale(1.2);
        }

        70% {
          transform: scale(0.9);
        }

        100% {
          transform: scale(1);
        }
      }

      .sync-status {
        padding: 10px 15px;
        border-radius: 4px;
        margin: 15px 0;

        &.info {
          background-color: #e3f2fd;
          color: #0d47a1;
        }

        &.success {
          background-color: #e8f5e9;
          color: #1b5e20;
        }

        &.error {
          background-color: #ffebee;
          color: #b71c1c;
        }
      }

      .sync-response {
        margin-top: 15px;

        h4 {
          margin-bottom: 5px;
          color: #333;
        }

        pre {
          background-color: #f5f5f5;
          padding: 10px;
          border-radius: 4px;
          overflow-x: auto;
          font-size: 12px;
          max-height: 300px;
          overflow-y: auto;
        }
      }
    }
  }
}
</style>